<template>
  <li v-if="position" :class="classes.component" :style="imageStyle">
    <div
      :class="$style.wrapper"
      ref="wrapper"
      @mouseenter="mouseEnter"
      @mouseleave="mouseLeave"
    >
      <router-link :to="routerLink" :key="listIndex" :class="$style.link">
        <base-image :image="image" :class="$style.image" :sizes="sizes" />
      </router-link>
    </div>
    <image-description :image="image" :class="$style.description" />
  </li>
</template>

<script>
import BaseImage from '@/components/base-image'
import ImageDescription from '@/components/image-description'
import { mapState } from 'vuex'
import { gsap } from 'gsap'

export default {
  components: {
    BaseImage,
    ImageDescription
  },
  props: {
    image: {
      type: Object,
      required: true
    },
    position: {
      type: Object,
      default: null
    },
    dimension: {
      type: Object,
      default: null
    },
    container: {
      type: Object,
      required: true
    },
    clickedIndex: {
      type: Number,
      default: null
    },
    listIndex: {
      type: Number,
      required: true
    },
    itemClicked: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      query: this.image.index,
      duration: 1,
      hoverDuration: 1,
      hoverScale: 1.1
    }
  },
  computed: {
    ...mapState(['isDesktop']),
    classes() {
      return {
        component: [
          this.$style.component,
          this.isCurrent ? this.$style['is-current'] : '',
          this.itemClicked ? this.$style['is-transitioning'] : ''
        ]
      }
    },
    isCurrent() {
      return this.clickedIndex === this.listIndex
    },
    imageStyle() {
      return {
        top: this.position.top + 'px',
        left: this.position.left + 'px',
        width: this.dimension.width + 'px'
      }
    },
    routerLink() {
      return {
        path: this.image.artist.url,
        query: { slide: this.query }
      }
    },
    sizes() {
      // return this.dimension.width + 'px'
      // NOTE: Load higher resolution image in advance to a possible scaleUp() transition
      return '50vw'
    }
  },
  methods: {
    mouseEnter() {
      const el = this.$refs.wrapper
      if (!el) return

      gsap.fromTo(
        el,
        this.hoverDuration,
        {
          x: 0,
          y: 0,
          xPercent: 0,
          yPercent: 0,
          scale: 1
        },
        {
          scale: this.hoverScale,
          ease: 'back.out(1.7)'
        }
      )
    },
    mouseLeave() {
      // Make sure that we don’t scale back as soon as transition has started
      if (this.itemClicked) return

      const el = this.$refs.wrapper
      if (!el) return

      gsap.fromTo(
        el,
        this.hoverDuration,
        { scale: this.hoverScale },
        { scale: 1, ease: 'back.out(1.7)' }
      )
    },
    scaleUp() {
      const el = this.$refs.wrapper
      if (!el) return

      // NOTE: Don’t use this.container for calculation of paddings since it’s the paddings in the artist view that actually count for the final image size
      // TODO: Don’t hardcode paddings of artist view since they might change
      const verticalPadding = 36
      const horizontalPadding = this.isDesktop ? 178 : 228

      const end = {
        x: this.container.width / 2 - this.container.left - this.position.left,
        y: this.container.height / 2 - this.container.top - this.position.top
      }

      const width = window.innerWidth - verticalPadding
      const height = window.innerHeight - horizontalPadding

      gsap.fromTo(
        el,
        this.duration,
        {
          x: 0,
          y: 0,
          xPercent: 0,
          yPercent: 0,
          scale: this.hoverScale,
          autoAlpha: 1
        },
        {
          x: end.x,
          y: end.y,
          // NOTE: Setting x/yPercent results in a curved transition
          xPercent: -50,
          yPercent: -50,
          scale: 1,
          width: width,
          height: height,
          ease: 'Power1.easeInOut'
        }
      )
    },
    scaleDown() {
      const el = this.$refs.wrapper
      if (!el) return
      const end = {
        x: this.container.width / 2 - this.container.left - this.position.left,
        y: this.container.height / 2 - this.container.top - this.position.top
      }

      gsap.fromTo(
        el,
        this.duration,
        {
          x: 0,
          y: 0,
          xPercent: 0,
          yPercent: 0,
          scale: 1,
          autoAlpha: 1
        },
        {
          x: end.x,
          y: end.y,
          // NOTE: Setting x/yPercent results in a curved transition
          xPercent: -50,
          yPercent: -50,
          scale: 0,
          autoAlpha: 0,
          delay: gsap.utils.random(0, 35) / 100,
          ease: 'Power1.easeInOut'
        }
      )
    },
    // Clear all properties that have been set by scaleUp/Down()
    clearProps() {
      const el = this.$refs.wrapper
      if (!el) return
      el.style.width = ''
      el.style.height = ''
      el.style.transform = ''
      el.style.opacity = ''
      el.style.visibility = ''
    }
  },
  watch: {
    itemClicked() {
      if (this.itemClicked) {
        if (this.isCurrent) {
          this.scaleUp()
        } else {
          this.scaleDown()
        }
      }
    }
  },
  activated() {
    this.clearProps()
  }
}
</script>

<style lang="scss" module>
.component {
  position: absolute;

  &.is-current {
    z-index: 1;
  }
}

.wrapper {
  @media #{$mqHover} {
    &:hover {
      ~ .description {
        opacity: 1;
        visibility: visible;
      }
    }
  }
}

.link {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    // filter: drop-shadow(1px 1px 3px rgba(0, 0, 0, 0.35));
    // transition: filter var(--xxlong) var(--ease-out-back);
  }

  // @media #{$mqHover} {
  //   &:hover {
  //     img {
  //       filter: drop-shadow(1px 1px 15px rgba(0, 0, 0, 0.35));
  //     }
  //   }
  // }

  // Remove drop shadow during scaleUp()
  // .is-transitioning & {
  //   img {
  //     filter: drop-shadow(0 0 0 rgba(0, 0, 0, 0.35));
  //   }
  // }
}

.description {
  opacity: 0;
  visibility: hidden;
  transition: opacity var(--long) ease, visibility var(--long) ease;
}
</style>
